import { Box, IconButton, Stack } from "@mui/material";
import {
  Close as CloseIcon,
  OpenInFull as OpenInFullIcon,
} from "@mui/icons-material";
import { createPortal } from "react-dom";
import { useEffect, useRef, useState } from "react";
import { RoomParams } from "../types";
import { VIDEO_BG } from "../styles";
import TelehealthRoom from "./TelehealthRoom";

/** Clamp a PiP position so the floating frame stays inside the viewport
 *  even on first paint and after a window resize. */
function clampToViewport(
  pos: { x: number; y: number },
  w: number,
  h: number
): { x: number; y: number } {
  const maxX = Math.max(0, window.innerWidth - w);
  const maxY = Math.max(0, window.innerHeight - h);
  return {
    x: Math.min(Math.max(pos.x, 0), maxX),
    y: Math.min(Math.max(pos.y, 0), maxY),
  };
}

interface Props {
  open: boolean;
  params: Omit<RoomParams, "role"> & { role?: RoomParams["role"] };
  onClose: () => void;
  /** True when mounted at the public guest route — TelehealthRoom uses this
   *  to fetch the session signature without an authenticated config. */
  isPublicView?: boolean;
  /** Host action bar callbacks. Forwarded to TelehealthRoom; bar hides when
   *  any are omitted. The overlay also auto-enters mini-mode on Start
   *  Encounter so the host can keep the call running while navigating. */
  patientName?: string;
  providerName?: string;
  onCancelAppointment?: (reason: string) => Promise<void> | void;
  onNoShow?: () => Promise<void> | void;
  onStartEncounter?: () => Promise<void> | void;
  /** Fires whenever the overlay transitions into mini-mode (PiP button or
   *  Start Encounter). Lets the parent navigate alongside the shrink so
   *  the host lands on the encounter screen with the call as a PiP. */
  onMinimize?: () => void;
  /** When false, the in-call PiP affordance is hidden — used to keep the
   *  open public guest URL and DOB-gated patient links from offering a
   *  feature that's only meaningful inside the provider/patient portal,
   *  where the user has somewhere to navigate to while the call shrinks. */
  allowMiniMode?: boolean;
}

/**
 * Hosts the conference UI inside the eCareHealth app. Two render modes:
 *   - full-screen   — fixed overlay covering the app
 *   - mini-window   — small draggable floating panel; the rest of the app is
 *                     fully interactive while the meeting keeps running
 *
 * The conference DOM stays at a stable position in the JSX tree across
 * mode switches so React's reconciler keeps `TelehealthRoom` mounted —
 * otherwise the SDK session leaves and rejoins, flashing "Reconnecting".
 */
export default function TelehealthOverlay({
  open,
  params,
  onClose,
  isPublicView = false,
  patientName,
  providerName,
  onCancelAppointment,
  onNoShow,
  onStartEncounter,
  onMinimize,
  allowMiniMode = true,
}: Props) {
  const [miniMode, setMiniMode] = useState(false);
  // PiP frame is bigger than before so the host can actually see the call
  // when it transitions out of fullscreen — small 360×240 boxes were easy
  // to miss against a busy encounter page.
  const PIP_W = 420;
  const PIP_H = 280;
  const [pipPos, setPipPos] = useState(() =>
    clampToViewport({ x: window.innerWidth - PIP_W - 24, y: 80 }, PIP_W, PIP_H)
  );
  const draggingRef = useRef<{ dx: number; dy: number } | null>(null);

  // Drag handler for the mini window — wired only when miniMode is on so
  // the listeners aren't sitting on window in fullscreen.
  useEffect(() => {
    if (!miniMode) return;
    const onMove = (e: MouseEvent) => {
      const drag = draggingRef.current;
      if (!drag) return;
      setPipPos(
        clampToViewport(
          { x: e.clientX - drag.dx, y: e.clientY - drag.dy },
          PIP_W,
          PIP_H
        )
      );
    };
    const onUp = () => (draggingRef.current = null);
    // Re-clamp on resize so a shrinking window doesn't push the PiP off-screen.
    const onResize = () =>
      setPipPos((cur) => clampToViewport(cur, PIP_W, PIP_H));
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("resize", onResize);
    };
  }, [miniMode]);

  if (!open) return null;

  const room: RoomParams = {
    room: params.room,
    role: params.role ?? "host",
    displayName: params.displayName,
    appointmentId: params.appointmentId,
    patientId: params.patientId,
    startTime: params.startTime,
    skipPreflight: params.skipPreflight,
  };

  const enterMiniMode = () => {
    setMiniMode(true);
    onMinimize?.();
  };

  const handleStartEncounter = onStartEncounter
    ? async () => {
        setMiniMode(true);
        await onStartEncounter();
      }
    : undefined;

  // The wrapper tree is identical in both modes — only the outer Box's sx
  // and the visibility of the PiP title bar change. The TelehealthRoom node
  // is always at the same JSX position, so React keeps it mounted across
  // the fullscreen ⇄ mini transition (no leave/rejoin).
  const fullscreenSx = {
    position: "fixed" as const,
    inset: 0,
    zIndex: 2000,
    bgcolor: VIDEO_BG,
    display: "flex",
    flexDirection: "column" as const,
  };
  // Bumped above any in-page modal/iframe overlays from the encounter
  // form (some page-level dialogs reach 9999); 11000 keeps the PiP
  // reliably on top so a host who clicks Start Encounter never loses
  // sight of the call behind a chart-level modal.
  const miniSx = {
    position: "fixed" as const,
    left: pipPos.x,
    top: pipPos.y,
    width: PIP_W,
    height: PIP_H,
    bgcolor: "background.paper",
    border: 2,
    borderColor: "primary.main",
    borderRadius: 2,
    boxShadow: 12,
    zIndex: 11000,
    display: "flex",
    flexDirection: "column" as const,
    overflow: "hidden",
  };

  return createPortal(
    <Box sx={miniMode ? miniSx : fullscreenSx}>
      {/* PiP title bar — kept in the tree always, hidden in fullscreen so
          adding/removing it doesn't shift the conference's child index. */}
      <Stack
        direction="row"
        onMouseDown={(e) => {
          draggingRef.current = {
            dx: e.clientX - pipPos.x,
            dy: e.clientY - pipPos.y,
          };
        }}
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          display: miniMode ? "flex" : "none",
          px: 1,
          py: 0.5,
          bgcolor: "background.default",
          borderBottom: 1,
          borderColor: "divider",
          cursor: "move",
          flexShrink: 0,
        }}
      >
        <Box component="span" sx={{ fontSize: 12, color: "text.secondary" }}>
          Telehealth
        </Box>
        <Stack direction="row">
          <IconButton size="small" onClick={() => setMiniMode(false)}>
            <OpenInFullIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      <Box sx={{ flex: 1, display: "flex", minHeight: 0, overflow: "hidden" }}>
        <TelehealthRoom
          params={room}
          isPublicView={isPublicView}
          onExit={onClose}
          isMiniMode={miniMode}
          onEnterMiniMode={allowMiniMode ? enterMiniMode : undefined}
          onExitMiniMode={() => setMiniMode(false)}
          patientName={patientName}
          providerName={providerName}
          onCancelAppointment={onCancelAppointment}
          onNoShow={onNoShow}
          onStartEncounter={handleStartEncounter}
        />
      </Box>
    </Box>,
    document.body
  );
}
